import {
  GroupDoc,
  UserSettingsDoc,
  UserSettingsGroupSettings
} from 'types/api/changes'

import {
  outlookPrefix,
  icloudPrefix,
  prefixGoogleEventsGroup
} from './constants'
import { APP_COLOR } from './colors'
import { CurrentConvertedCalendars } from './google_events'

export enum PersonalGroupSyncStatus {
  PENDING = '1',
  SYNCED = '2',
  PRIVATE = '3'
}
import { isTrulyEmpty } from './object'
import { getCachedConvertedCalendarsFromLocalStorage } from './third_party_calendars'

// Define the types of third-party providers you support
export enum ProviderType {
  GOOGLE = 'Google',
  OUTLOOK = 'Outlook',
  APPLE = 'iCloud'
  // Add more providers here
}

export function getGroupColor(
  groupDoc: GroupDoc | undefined | null,
  groupsSettings: Record<string, UserSettingsGroupSettings> | undefined
) {
  if (!groupDoc) return APP_COLOR

  const groupItselfColor = groupDoc.GroupColor

  return groupsSettings?.[groupDoc._id ?? '']?.GroupColor ?? groupItselfColor
}

export function defaultGroupId(userSettings?: UserSettingsDoc) {
  if (!userSettings) return undefined

  return Object.entries(userSettings?.GroupsSettings ?? {}).find(
    ([key, value]) => value.Default === '1'
  )?.[0]
}

export function isUsersCanAddEvents(group: GroupDoc | undefined) {
  if (!group) return false
  return group.GroupSettings?.IsAllParticipantsCanAddItems === '1'
}

/**
 *
 * @param group to check if it is a converted group
 * @returns true if id contains prefix of a converted group
 */
export function isConvertedGroup(group: GroupDoc | undefined) {
  if (!group) return false
  return (
    isConvertedGroupId(group._id?.toLowerCase() ?? '') ||
    isConvertedGroupId(group.CalendarEmail?.toLocaleLowerCase() ?? '')
  )
}

export function isConvertedGroupId(groupId?: string) {
  if (isTrulyEmpty(groupId)) return false

  const calendar = getCachedConvertedCalendarsFromLocalStorage().find(
    (cal) => cal.localId === groupId
  )

  if (calendar) return true
  return [
    prefixGoogleEventsGroup.toLowerCase(),
    outlookPrefix.toLowerCase(),
    icloudPrefix.toLowerCase()
  ].some((prefix) => groupId?.toLowerCase().includes(prefix))
}

export function groupIdToProviderTypeName(groupId?: string) {
  const calendar = getCachedConvertedCalendarsFromLocalStorage().find(
    (cal) => cal.localId === groupId
  )

  if (calendar) {
    if (calendar.prefix === prefixGoogleEventsGroup) return 'Google'
    if (calendar.prefix === outlookPrefix) return 'Outlook'
    if (calendar.prefix === icloudPrefix) return 'iCloud'
    return undefined
  }

  if (!groupId) return undefined
  if (groupId.includes(outlookPrefix)) return 'Outlook'
  if (groupId.includes(prefixGoogleEventsGroup)) return 'Google'
  if (groupId.includes(icloudPrefix)) return 'iCloud'

  return undefined
}

export function groupIdToProvider(groupId: string): ProviderType | undefined {
  const calendar = getCachedConvertedCalendarsFromLocalStorage().find(
    (cal) => cal.localId === groupId
  )

  if (calendar) {
    if (calendar.prefix === prefixGoogleEventsGroup) return ProviderType.GOOGLE
    if (calendar.prefix === outlookPrefix) return ProviderType.OUTLOOK
    if (calendar.prefix === icloudPrefix) return ProviderType.APPLE
    return undefined
  }

  if (!groupId) return undefined
  if (groupId.includes(prefixGoogleEventsGroup)) return ProviderType.GOOGLE
  if (groupId.includes(outlookPrefix)) return ProviderType.OUTLOOK
  if (groupId.includes(icloudPrefix)) return ProviderType.APPLE
  return undefined
}

export function groupIdToPrefix(groupId: string): string | undefined {
  if (!groupId) return undefined
  if (groupId.includes(prefixGoogleEventsGroup)) return prefixGoogleEventsGroup
  if (groupId.includes(outlookPrefix)) return outlookPrefix
  if (groupId.includes(icloudPrefix)) return icloudPrefix
  return undefined
}

export function groupToProvider(
  group: GroupDoc | undefined
): ProviderType | undefined {
  if (!group) return undefined

  if (
    group.CalendarEmail?.includes(prefixGoogleEventsGroup) ||
    group._id?.includes(prefixGoogleEventsGroup)
  )
    return ProviderType.GOOGLE
  if (
    group.CalendarEmail?.includes(outlookPrefix) ||
    group._id?.includes(outlookPrefix)
  )
    return ProviderType.OUTLOOK
  if (
    group.CalendarEmail?.includes(icloudPrefix) ||
    group._id?.includes(icloudPrefix)
  )
    return ProviderType.APPLE
  return undefined
}
